import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { buildCommercialInlineParts, buildCommercialTooltipLines } from '../commercialSummary';
import StageDot from './StageDot';
import { colors } from '../../styles/theme';

const TEXT_SX = { fontSize: '12px', lineHeight: '16px', color: '#ABABAB' };
const ELLIPSIS = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

/**
 * Línea secundaria de las cards de registros (la ubicación va en texto,
 * `RecordLotIdentity`): texto `primary` opcional (meta de UF / lista · precio CAD / consultas de la lista) y el
 * resumen comercial compacto (punto de etapa · etapa · leads · consultas ·
 * pill de sin leer · actividad · ⚠). Lote con UF (`kind="rollup"`): sin punto ni
 * etapa, desglose en texto (`formatRollupBreakdown`). Una sola línea con ellipsis; tooltip completo.
 * Sin resumen / `sin_interes` sin señales → solo `primary` (o `fallback`; si no, null).
 * Con `showZero` y bloque presente sin señales → «0 leads · 0 consultas» en gris.
 * @see docs/frontend/commercial-summary.md
 */
export default function RecordCommercialLine({
  primary = '',
  summary = null,
  kind = 'summary',
  isCad = false,
  hideUnseen = false,
  fallback = '',
  /** Lote con UF: `commercial_summary` del lote catastral (su etapa suma al desglose). */
  lotSummary = null,
  /**
   * Bloque presente sin señal (mínimo / `sin_interes`): «N leads · N consultas» en gris,
   * sin punto ni etapa. Sin bloque (no se pidió o falló) → nada: no se inventa el 0.
   */
  showZero = false,
}) {
  const inline = buildCommercialInlineParts(summary, { kind, isCad, hideUnseen, lotSummary });
  const primaryText = primary || (inline ? '' : fallback);
  if (!inline) {
    if (!primaryText && showZero && summary && typeof summary === 'object') {
      const leads = Number(summary.leads_count) || 0;
      const inquiries = Number(summary.inquiries_count) || 0;
      const text = `${leads} lead${leads === 1 ? '' : 's'} · ${inquiries} consulta${inquiries === 1 ? '' : 's'}`;
      return (
        <Typography data-testid="record-line-zero" sx={{ ...TEXT_SX, ...ELLIPSIS, minWidth: 0 }}>
          {text}
        </Typography>
      );
    }
    if (!primaryText) return null;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, overflow: 'hidden' }}>
        {primaryText ? (
          <Typography data-testid="record-line-primary" sx={{ ...TEXT_SX, ...ELLIPSIS, minWidth: 0 }}>{primaryText}</Typography>
        ) : null}
      </Box>
    );
  }
  const lines = buildCommercialTooltipLines(summary, { kind, lotSummary });
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, overflow: 'hidden' }}>
      {primaryText ? (
        <>
          <Typography
            data-testid="record-line-primary"
            sx={{ ...TEXT_SX, ...ELLIPSIS, flexShrink: 1, minWidth: 24, maxWidth: '55%' }}
          >
            {primaryText}
          </Typography>
          <Typography component="span" sx={{ ...TEXT_SX, flexShrink: 0 }} aria-hidden>·</Typography>
        </>
      ) : null}
      <Tooltip arrow placement="top" title={<Box>{lines.map((l) => <div key={l}>{l}</div>)}</Box>}>
        <Box
          data-testid="record-line-commercial"
          data-status={inline.breakdown ? undefined : inline.status}
          data-breakdown={inline.breakdown ? 'true' : undefined}
          aria-label={lines.join('. ')}
          onClick={(e) => e.stopPropagation()}
          sx={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0, flex: '1 1 auto', cursor: 'default' }}
        >
          {inline.color ? <StageDot color={inline.color} data-testid="record-line-dot" /> : null}
          <Typography component="span" sx={{ ...TEXT_SX, ...ELLIPSIS, color: '#D4D4D4', minWidth: 0 }}>
            {[inline.label, ...inline.parts].filter(Boolean).join(' · ')}
          </Typography>
          {inline.unseen > 0 ? (
            // Pill (como el badge de Leads y consultas): no se confunde con el texto
            // cortado («… 1») y nunca se recorta.
            <Box
              component="span"
              data-testid="record-line-unseen"
              aria-label={`${inline.unseen} sin leer`}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                height: 14,
                minWidth: 14,
                px: '4px',
                boxSizing: 'border-box',
                borderRadius: '999px',
                bgcolor: colors.primary,
                color: colors.textPrimary,
                fontSize: '10px',
                lineHeight: '14px',
                fontWeight: 600,
              }}
            >
              {inline.unseen > 99 ? '99+' : inline.unseen}
            </Box>
          ) : null}
          {inline.mismatchMark ? (
            <ReportProblemOutlinedIcon data-testid="record-line-mismatch" sx={{ fontSize: 12, color: '#ABABAB', flexShrink: 0 }} />
          ) : null}
        </Box>
      </Tooltip>
    </Box>
  );
}
